/**
 * Campaign Tracker Constants
 *
 * Cookie configuration and environment-specific settings.
 * Part of BEC-20520: Campaign Tracker with Salesforce
 */

import { isEnvProduction } from '@/lib/configuration/configuration';
import { Brand } from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';

/** Cookie name for storing campaign UTM data */
export const CAMPAIGN_COOKIE_NAME = 'breitling_campaign';

/** Default max-age for production: 14 days in seconds */
const DEFAULT_MAX_AGE_SECONDS = 14 * 24 * 60 * 60; // 1,209,600 seconds

/** Non-production max-age: 1 hour in seconds (INT/TST/PRE) */
const DEV_MAX_AGE_SECONDS = 60 * 60; // 3,600 seconds

/**
 * Get the production cookie domain based on environment variable or brand.
 *
 * Priority:
 * 1. NEXT_PUBLIC_CAMPAIGN_COOKIE_DOMAIN env var (if set)
 * 2. Derived from BRAND (e.g., '.gallet.com' for Gallet, '.breitling.com' for Breitling)
 * 3. Default to '.breitling.com' for backward compatibility
 *
 * The domain must start with '.' to allow subdomain sharing (e.g., '.breitling.com'
 * works for both breitling.com and www.breitling.com).
 *
 * @returns Cookie domain string (e.g., '.breitling.com' or '.gallet.com')
 */
export const getCampaignCookieDomain = (): string => {
  // Priority 1: Environment variable override (most flexible)
  const envDomain = process.env.NEXT_PUBLIC_CAMPAIGN_COOKIE_DOMAIN;
  if (envDomain) {
    // Ensure domain starts with '.' for subdomain sharing
    return envDomain.startsWith('.') ? envDomain : `.${envDomain}`;
  }

  // Priority 2: Derive from BRAND
  if (BRAND === Brand.Gallet) {
    return '.gallet.com';
  }

  // Priority 3: Default to Breitling (backward compatibility)
  return '.breitling.com';
};

/** UTM parameters we track */
export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

/**
 * Get the cookie max-age.
 *
 * - PRD: 14 days (default)
 * - INT/TST/PRE: 1 hour
 * - Override via NEXT_PUBLIC_CAMPAIGN_COOKIE_MAX_AGE takes precedence
 *
 * @returns Max-age in seconds
 */
export const getCampaignCookieMaxAge = (): number => {
  // Allow override via environment variable
  const envMaxAge = process.env.NEXT_PUBLIC_CAMPAIGN_COOKIE_MAX_AGE;
  if (envMaxAge) {
    const parsed = parseInt(envMaxAge, 10);
    // Only use if valid positive number
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
    // Invalid value: fall through to default
  }

  // 1 hour for non-production environments (INT/TST/PRE)
  if (!isEnvProduction()) {
    return DEV_MAX_AGE_SECONDS;
  }

  return DEFAULT_MAX_AGE_SECONDS;
};

/**
 * Build the cookie attribute string based on environment.
 *
 * - PRD: includes Secure and domain (from getCampaignCookieDomain())
 * - Non-PRD/localhost: host-only, no Secure (so it works locally)
 *
 * @param maxAge - Cookie max-age in seconds
 * @returns Cookie attribute string (e.g., "path=/; max-age=3600; SameSite=Lax")
 */
export const getCookieAttributes = (maxAge: number): string => {
  const baseAttributes = `path=/; max-age=${maxAge}; SameSite=Lax`;

  if (isEnvProduction()) {
    const cookieDomain = getCampaignCookieDomain();
    return `${baseAttributes}; Secure; domain=${cookieDomain}`;
  }

  return baseAttributes;
};
